// Sprint 12.5 — unified branded error / honest-state surface (atlas Errors page).
// One full-height composition for every failure: calm explanation + branded motif +
// primary & secondary recovery + a recovery band. No dead lower viewport. Replaces the
// scattered crash/offline/not-found one-offs. §S680: every variant's code/title/body
// and every button/band label resolves through t() ('state.*' namespace) at render
// time, so the surface is natural Hebrew under RTL; callers can still pass explicit
// title/body/labels (server-provided messages render verbatim).
import type { JSX } from 'preact';
import { AlertTriangle, Compass, WifiOff, Lock, SearchX, FileQuestion, RefreshCw } from 'lucide-preact';
import type { LucideIcon } from 'lucide-preact';
import { PomelaMascot } from './PomelaMascot';
import { t } from '../lib/i18n';

export type ErrorVariant = 'notFound' | 'appNotFound' | 'generationFailed' | 'permissionDenied' | 'offline' | 'noResults' | 'crash' | 'generic';

interface VariantDef { codeKey: string; titleKey: string; bodyKey: string; Icon: LucideIcon; }
const VARIANTS: Record<ErrorVariant, VariantDef> = {
  notFound: { codeKey: '404', titleKey: 'state.notFound.title', bodyKey: 'state.notFound.body', Icon: Compass },
  appNotFound: { codeKey: 'state.appNotFound.code', titleKey: 'state.appNotFound.title', bodyKey: 'state.appNotFound.body', Icon: FileQuestion },
  generationFailed: { codeKey: 'state.genFailed.code', titleKey: 'state.genFailed.title', bodyKey: 'state.genFailed.body', Icon: AlertTriangle },
  permissionDenied: { codeKey: 'state.denied.code', titleKey: 'state.denied.title', bodyKey: 'state.denied.body', Icon: Lock },
  offline: { codeKey: 'state.offline.code', titleKey: 'state.offline.title', bodyKey: 'state.offline.body', Icon: WifiOff },
  noResults: { codeKey: 'state.noResults.code', titleKey: 'state.noResults.title', bodyKey: 'state.noResults.body', Icon: SearchX },
  crash: { codeKey: 'state.crash.code', titleKey: 'state.crash.title', bodyKey: 'state.crash.body', Icon: AlertTriangle },
  generic: { codeKey: 'state.generic.code', titleKey: 'state.generic.title', bodyKey: 'state.generic.body', Icon: AlertTriangle }
};

export function ErrorState({ variant = 'generic', title, body, primaryLabel, onPrimary, secondaryLabel, onSecondary, showBand = true }: {
  variant?: ErrorVariant;
  title?: string; body?: string;
  primaryLabel?: string; onPrimary?: () => void;
  secondaryLabel?: string; onSecondary?: () => void;
  showBand?: boolean;
}): JSX.Element {
  const def = VARIANTS[variant];
  const primary = onPrimary ?? (() => { window.location.assign(window.location.pathname); });
  const retryish = variant === 'offline' || variant === 'crash' || variant === 'generationFailed';
  const pLabel = primaryLabel ?? (retryish ? t('common.retry') : t('state.goHome'));
  return (
    <div className="pf-error" role="alert">
      <span className="pf-error__brand pf-mark-halo" aria-hidden="true">
        <PomelaMascot size={64} />
        <span className="pf-error__vbadge"><def.Icon size={15} /></span>
      </span>
      <span className="pf-error__code">{def.codeKey === '404' ? '404' : t(def.codeKey)}</span>
      <h1 className="pf-error__title">{title ?? t(def.titleKey)}</h1>
      <p className="pf-error__body" dir="auto">{body ?? t(def.bodyKey)}</p>
      <div className="pf-error__actions">
        <button type="button" className="pf-btn pf-btn--grad" onClick={primary}>
          {retryish ? <RefreshCw size={15} /> : null} {pLabel}
        </button>
        {(secondaryLabel || onSecondary) && (
          <button type="button" className="pf-btn" onClick={onSecondary ?? (() => { location.hash = ''; })}>{secondaryLabel ?? t('common.back')}</button>
        )}
      </div>
      {showBand && (
        <div className="pf-error__band">
          <a className="pf-error__link" href="#">{t('common.home')}</a>
          <a className="pf-error__link" href="#apps">{t('sb.workspace.title')}</a>
          <a className="pf-error__link" href="#guide">{t('state.guide')}</a>
          <a className="pf-error__link" href="#help">{t('sb.help.title')}</a>
        </div>
      )}
    </div>
  );
}
